/** One sortable row in Settings → Pipeline Stages. */
import { useEffect, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronUp, GripVertical, Trash2 } from 'lucide-react';
import { truthy, type Stage } from '@/features/pipeline/types';
import { ColorSwatches } from './ColorSwatches';
import { DEFAULT_STAGE_COLOR, STAGE_KINDS } from './stage-colors';
import type { StageAdmin } from './use-stage-admin';

const iconBtn =
  'flex h-10 w-10 items-center justify-center rounded-md text-gray-500 hover:bg-page hover:text-gray-800 disabled:opacity-30 disabled:hover:bg-transparent';

export function StageRow({
  stage,
  index,
  total,
  count,
  admin,
}: {
  stage: Stage;
  index: number;
  total: number;
  count: number;
  admin: StageAdmin;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: stage.id });
  const [name, setName] = useState(stage.name);
  useEffect(() => setName(stage.name), [stage.name]);

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`rounded-lg border bg-white p-3 ${isDragging ? 'z-10 border-navy shadow-lg' : 'border-gray-200 shadow-sm'}`}
      data-testid={`stage-row-${stage.id}`}
    >
      {/* Wraps so that on phones the move/delete buttons drop to their own row under the swatches instead of a narrow vertical stack beside a truncated name (Stage 7 design pass). */}
      <div className="flex flex-wrap items-start gap-2">
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          aria-label={`Drag to reorder ${stage.name}`}
          className={`${iconBtn} cursor-grab touch-none active:cursor-grabbing`}
        >
          <GripVertical size={16} strokeWidth={2} aria-hidden />
        </button>
        <span
          className="mt-3 h-3.5 w-3.5 shrink-0 rounded-full"
          style={{ backgroundColor: stage.color ?? DEFAULT_STAGE_COLOR }}
          aria-hidden
        />
        <div className="min-w-0 flex-1 basis-[200px]">
          <div className="flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor={`stage-name-${stage.id}`}>
              Stage name
            </label>
            <input
              id={`stage-name-${stage.id}`}
              data-stage-heading={stage.id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => admin.rename(stage, name)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') setName(stage.name);
              }}
              className="min-h-10 min-w-0 flex-1 basis-[200px] rounded-md border border-transparent px-2 text-sm font-medium text-gray-900 hover:border-gray-200 focus:border-navy"
            />
            <label className="sr-only" htmlFor={`stage-kind-${stage.id}`}>
              Stage type
            </label>
            <select
              id={`stage-kind-${stage.id}`}
              value={stage.kind}
              onChange={(e) => admin.setKind(stage, e.target.value as Stage['kind'])}
              className="min-h-10 rounded-md border border-gray-200 bg-white px-2 text-[13px]"
            >
              {STAGE_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
            <ColorSwatches value={stage.color} onChange={(hex) => admin.recolor(stage, hex)} label={`Colour for ${stage.name}`} />
            <span className="text-[12px] tabular-nums text-muted">
              {count} candidate{count === 1 ? '' : 's'}
            </span>
            {truthy(stage.is_entry) && (
              <span className="rounded bg-info-blue-bg px-1.5 py-0.5 text-[12px] font-medium text-info-blue">Entry stage</span>
            )}
            {stage.system_key && (
              <span className="mono rounded bg-page px-1.5 py-0.5 text-[12px] text-gray-700" title="Automation key">
                {stage.system_key}
              </span>
            )}
          </div>
        </div>
        <div className="flex basis-full justify-end sm:basis-auto sm:shrink-0">
          <button type="button" disabled={index === 0} onClick={() => admin.move(stage, -1)} aria-label={`Move ${stage.name} up`} className={iconBtn}>
            <ChevronUp size={16} strokeWidth={2} aria-hidden />
          </button>
          <button type="button" disabled={index === total - 1} onClick={() => admin.move(stage, 1)} aria-label={`Move ${stage.name} down`} className={iconBtn}>
            <ChevronDown size={16} strokeWidth={2} aria-hidden />
          </button>
          <button type="button" onClick={() => admin.requestDelete(stage, count)} aria-label={`Delete ${stage.name}`} className={`${iconBtn} hover:text-danger`}>
            <Trash2 size={16} strokeWidth={2} aria-hidden />
          </button>
        </div>
      </div>
    </li>
  );
}
